/**
 * CropCard Component
 * A clean, minimal card presenting a single crop's trap-lamp preset:
 * category, target pest, ON/OFF schedule with computed duration, light
 * spectrum, affected regions, and an expandable panel with detailed insights.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.crop - Crop record (name, pest, on, off, light, region, category, details)
 * @param {string} props.language - Language code (en, hi, gu) for localization
 * @returns {JSX.Element}
 */
import React from 'react';
import { MdBugReport, MdWbIncandescent, MdPlace } from 'react-icons/md';
import { translations } from '../constants/translations';
import { CATEGORY_COLORS } from '../utils/colorScheme';
import { calculateDuration, getLocalizedCrop } from '../utils/cropHelpers';

const titleCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const CropCard = ({ crop, language }) => {
  const t = translations[language];
  const { name, pest } = getLocalizedCrop(crop, language);

  // Data categories are upper-case (e.g. "GRAIN"); colour map keys are title-case.
  const category = t.catNames?.[crop.category] || titleCase(crop.category);
  const accent = (CATEGORY_COLORS[crop.category] || CATEGORY_COLORS[titleCase(crop.category)] || CATEGORY_COLORS.General).bg;
  const isHeat = crop.light?.includes('Heat');

  return (
    <div className="group flex flex-col h-full bg-white border border-slate-200 rounded-2xl p-5 transition-all duration-300 hover:border-slate-300 hover:shadow-lg">

      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {category}
        </span>
      </div>
      <h3 className="text-lg font-bold text-slate-900 leading-snug break-words mb-4">
        {name}
      </h3>

      {/* Target pest */}
      <div className="mb-4">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
          <MdBugReport className="w-3.5 h-3.5" /> {t.pest}
        </p>
        <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
          {pest}
        </p>
      </div>

      {/* Schedule */}
      <div className="grid grid-cols-3 rounded-xl border border-slate-200 divide-x divide-slate-200 overflow-hidden mb-4">
        <div className="px-2 py-2.5 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{t.on}</p>
          <p className="text-sm font-bold text-emerald-700">{crop.on}</p>
        </div>
        <div className="px-2 py-2.5 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{t.off}</p>
          <p className="text-sm font-bold text-slate-700">{crop.off}</p>
        </div>
        <div className="px-2 py-2.5 text-center bg-slate-50">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{t.duration}</p>
          <p className="text-sm font-bold text-slate-700">{calculateDuration(crop.on, crop.off)}</p>
        </div>
      </div>

      {/* Meta: light + regions */}
      <div className="space-y-2.5 mt-auto">
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <MdWbIncandescent className="w-3.5 h-3.5" /> {t.lightSpectrum}
          </span>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-md ${
              isHeat ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {crop.light}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">
            <MdPlace className="w-3.5 h-3.5" /> {t.affectedRegions}
          </span>
          <span className="text-xs font-medium text-slate-600 text-right">{crop.region}</span>
        </div>
      </div>
    </div>
  );
};

export default CropCard;
